import React from "react";

export function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

export function Panel({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-white/10 bg-white/[0.04] shadow-[0_0_0_1px_rgba(255,255,255,0.02)] backdrop-blur-xl",
        className
      )}
    >
      {children}
    </div>
  );
}

export function Card({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.03] shadow-[0_14px_40px_-26px_rgba(0,0,0,0.8)]",
        className
      )}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  onClick,
  className,
  variant = "default",
  size = "md",
  disabled,
  title,
  type,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: "default" | "ghost" | "outline" | "primary";
  size?: "sm" | "md" | "lg" | "icon";
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit" | "reset";
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/30 disabled:opacity-50 disabled:pointer-events-none";
  const sizes: Record<typeof size, string> = {
    sm: "h-8 px-3 text-xs",
    md: "h-9 px-4 text-sm",
    lg: "h-10 px-5 text-sm",
    icon: "h-9 w-9",
  };
  const variants: Record<typeof variant, string> = {
    default: "bg-white/5 text-white hover:bg-white/8 border border-white/10",
    outline:
      "bg-transparent border border-white/15 text-white hover:bg-white/5",
    ghost: "bg-transparent text-white/80 hover:text-white hover:bg-white/5",
    primary:
      "bg-gradient-to-r from-cyan-500/80 to-blue-500/80 text-white border border-white/15 hover:from-cyan-400/90 hover:to-blue-400/90 shadow-[0_10px_30px_-16px_rgba(34,211,238,0.7)]",
  };
  return (
    <button
      type={type ?? "button"}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(base, sizes[size], variants[variant], className)}
    >
      {children}
    </button>
  );
}

export function Pill({
  children,
  tone = "neutral",
  className,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "green" | "red" | "blue" | "cyan";
  className?: string;
}) {
  const tones: Record<typeof tone, string> = {
    neutral: "border-white/10 bg-white/5 text-white/70",
    green: "border-emerald-400/25 bg-emerald-400/10 text-emerald-200",
    red: "border-rose-400/25 bg-rose-400/10 text-rose-200",
    blue: "border-blue-400/25 bg-blue-400/10 text-blue-200",
    cyan: "border-cyan-400/25 bg-cyan-400/10 text-cyan-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-none",
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

export function Dot({ color }: { color: "cyan" | "red" | "orange" }) {
  const c =
    color === "cyan"
      ? "bg-cyan-300"
      : color === "red"
      ? "bg-rose-400"
      : "bg-orange-300";
  return <span className={cn("inline-block h-2 w-2 rounded-full", c)} />;
}

export function Divider() {
  return <div className="h-px w-full bg-white/10" />;
}
